<script setup lang="ts">
import { onBeforeMount, onMounted, useTemplateRef, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import type { GenericArray } from '@Types';
import type { PaginateResult } from '@Providers';
import type { ProductAtCart } from '../../../types';
import { API_URL } from '@Constants';
import { useSessionStore } from '@Stores';
import { ROLES } from '@AuthModule';
import { useCleanSharedRefs, useDataTable, useDataTableFilters } from '@DataTableModule';
import { useActiveFilter, useFilters } from '@FilterModule';
import { GetProductListService } from '../../../services';
import { useProductsStore } from '../../../stores';
import productsAndServicesTableHelper from './productsAndServicesTableHelper';

defineExpose({ onGetProductListService });

const emits = defineEmits<{
  onAddToCart: [ProductAtCart];
  onUpdateCart: [];
}>();

defineProps<{
  hasHeader?: boolean;
  loadingButton?: boolean;
  total?: any;
}>();

const { can } = useSessionStore();
const { stats, totalItems } = storeToRefs(useProductsStore());

const { activeFilters } = useActiveFilter();
const { cleanDataTableSharedRefs } = useCleanSharedRefs();
const { columns, frozenFields, rows, rowsPerPage, totalRecords } = useDataTable();
const { initialColumns, activeColumns, mainRequestUrlParams, searchQuery } = useDataTableFilters();
const { clearFilters, getActiveFilters } = useFilters();
const { t } = useI18n();
const tablePageHeader = useTemplateRef('tablePageHeader');

const isEmptyTable = ref<boolean | undefined>(false);
const loading = ref(false);
const quantity = ref<GenericArray>({});
const refreshQuantityColumn = ref(false);

watch(searchQuery, () => refreshData());

onBeforeMount(() => cleanDataTableSharedRefs());
onMounted(() => onGetProductListService());

// EVENTS
async function onGetProductListService(isParentRefresh: boolean = false) {
  quantity.value = {};
  loading.value = !isParentRefresh;
  refreshQuantityColumn.value = isParentRefresh;

  await GetProductListService(rowsPerPage.value, mainRequestUrlParams.value)
    .then((result) => {
      _handler(result);
      _setColumns();

      if (result?.filters?.fields) _setFilters(result.filters.fields);
    })
    .finally(() => {
      loading.value = false;
      refreshQuantityColumn.value = false;
    });

  function _handler({ data, total, empty_table }: PaginateResult) {
    isEmptyTable.value = empty_table;
    rows.value = data;
    totalRecords.value = total;
    totalItems.value = total;
    stats.value = [
      {
        name: t('stats.products'),
        value: data.filter((item: any) => item.type === 'product').length.toString(),
      },
      {
        name: t('stats.services'),
        value: data.filter((item: any) => item.type === 'service').length.toString(),
      },
      {
        name: t('stats.total'),
        value: total.toString(),
      },
    ];
  }

  function _setColumns() {
    const staticColumns = [
      {
        name: 'sku',
        label: 'SKU#',
      },
      {
        name: 'category',
        label: 'Category',
      },
      // {
      //   name: 'photo',
      //   label: 'Photo',
      // },
      {
        name: 'product',
        label: 'Name',
      },
      {
        name: 'type',
        label: 'Type',
      },
      {
        name: 'price',
        label: 'Price',
      },
      // TODO :: AF-935 - Products part II
      // {
      //   name: 'in_stock',
      //   label: 'In Stock',
      // },
    ];

    const dynamicColumns = staticColumns.map((field: any) => productsAndServicesTableHelper(field));

    columns.value = dynamicColumns;
    initialColumns.value = dynamicColumns.map(({ field }) => field);
    activeColumns.value = initialColumns.value;

    frozenFields.value = ['sku'];
  }

  function _setFilters(fields: any) {
    activeFilters.value = getActiveFilters(fields);
  }
}

function onAddToCart(product: ProductAtCart) {
  const PAYLOAD = {
    ...product,
    quantity: quantity.value[product._id] || 0,
  };

  emits('onAddToCart', PAYLOAD);
}

async function refreshData() {
  loading.value = true;

  await onGetProductListService().finally(() => (loading.value = false));
}
</script>

<template>
  <header ref="tablePageHeader">
    <MHead
      v-if="hasHeader"
      compact
      :loading
      :stats
      :title="$t('productsAndServices.title')"
      :subtitle="$t('productsAndServices.description')"
    />

    <FiltersTemplate
      :loading
      :dialog="{ title: $t('animals.filters.title.inCare') }"
      :hasDataTableFilters="!!hasHeader"
      @onClearFilters="clearFilters() && refreshData()"
      @onApplyFilters="refreshData"
    >
      <template #slotRight>
        <Button
          v-if="totalItems > 0 && hasHeader"
          class="af-button af-button-success"
          icon="pi pi-cart-plus"
          :disabled="total === 0"
          :label="`${$t('cart.label.addItems')} (${total || 0})`"
          :loading="loadingButton"
          @click="emits('onUpdateCart')"
        />
      </template>
    </FiltersTemplate>
  </header>

  <MDataTable
    v-if="!isEmptyTable"
    :loading
    :class="{ 'mb-8': !hasHeader }"
    :hasAsideColumn="false"
    :hasSelector="false"
    :tunnelProps="{ header: 'Quantity' }"
    @onParamsUpdate="refreshData"
  >
    <template #tunnel="{ props }">
      <AInputNumber
        v-if="!refreshQuantityColumn"
        v-model="quantity[props._id]"
        :min="0"
        @onDebouncedModel="onAddToCart(props)"
      />

      <AInputNumber v-else />
    </template>
  </MDataTable>

  <MEmptySection
    v-else-if="isEmptyTable && !loading"
    class="mt-4 h-[62vh]"
    icon="pi pi-box"
    label="productsAndServices.emptyState.label"
  >
    <template #customAction>
      <a
        v-if="can(ROLES.OUTCOME_SETTINGS)"
        :href="`${API_URL}/shelter/new-ui/settings/products/manage-products`"
      >
        <Button
          class="af-button af-button-success h-9"
          icon="pi pi-plus"
          :label="$t('productsAndServices.emptyState.action')"
        />
      </a>
    </template>
  </MEmptySection>
</template>
